import * as z from 'zod';

export const CreateTicketDefinitionRequest = z.object({
  ticketDefinition: z
    .object({
      _id: z
        .string()
        .describe('Ticket definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      eventId: z
        .string()
        .describe('Event ID to which the ticket definition belongs.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ticket definition is updated. To prevent conflicting changes, the existing `revision` must be used when updating a ticket definition.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ticket definition was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ticket definition was updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Ticket definition name.').min(1).max(30),
      description: z
        .string()
        .describe('Ticket definition description.')
        .max(500)
        .optional()
        .nullable(),
      policyText: z
        .string()
        .describe('Ticket definition policy.')
        .max(1000)
        .optional()
        .nullable(),
      hidden: z
        .boolean()
        .describe(
          "Whether this ticket definition is hidden from site visitors and can't be purchased."
        )
        .optional(),
      limited: z
        .boolean()
        .describe('Whether the ticket has a limited maximum quantity.')
        .optional(),
      initialLimit: z
        .number()
        .int()
        .describe(
          'The maximum number of tickets that can be sold for the event when first defining the event. If a seating map is defined after you created a ticket definition, this property is ignored and `actualLimit` is used instead. To create unlimited tickets, skip this field in the request.'
        )
        .optional()
        .nullable(),
      actualLimit: z
        .number()
        .int()
        .describe(
          'The maximum number of tickets that can be sold for the event after adding a seating map to the event. If no seating map is defined, this property is the same as `initialLimit`.'
        )
        .optional()
        .nullable(),
      pricingMethod: z
        .intersection(
          z.object({
            pricingType: z.enum(['STANDARD', 'DONATION']).optional(),
            free: z
              .boolean()
              .describe(
                'Whether the ticket is free. To create a free ticket, enter `"0"` in `pricingMethod.fixedPrice.value`.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              guestPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
              fixedPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                    )
                    .optional(),
                })
                .describe('Same ticket price for everyone.'),
            }),
            z.object({
              fixedPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
              guestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                    )
                    .optional(),
                })
                .describe(
                  'Guests choose how much they\'d like to pay for the ticket. You can set the minimum price, or specify `"0"` in the request to make the ticket free. The price can be updated to a higher amount by a guest during the checkout.'
                ),
            }),
            z.object({
              fixedPrice: z.never().optional(),
              guestPrice: z.never().optional(),
              pricingOptions: z
                .object({
                  optionDetails: z
                    .array(
                      z.object({
                        optionId: z
                          .string()
                          .describe('Ticket price option ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe(
                            'Ticket price option name, such as "Child Ticket".'
                          )
                          .min(1)
                          .max(200)
                          .optional()
                          .nullable(),
                        price: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                              )
                              .optional(),
                          })
                          .describe('Ticket price.')
                          .optional(),
                      })
                    )
                    .max(100)
                    .optional(),
                })
                .describe(
                  'Sets of various ticket prices. For example, you can charge different prices for children and adults.'
                ),
            }),
          ])
        )
        .describe('Ticket pricing method.'),
      feeType: z.enum(['FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT', 'NO_FEE']),
      salePeriod: z
        .object({
          startDate: z
            .date()
            .describe(
              'Date and time the ticket sale starts. Rounded down to the nearest minute.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Date and time the ticket sale ends. Rounded down to the nearest minute.'
            )
            .optional()
            .nullable(),
          displayNotOnSale: z
            .boolean()
            .describe(
              "Whether to display the ticket if it's not available to buy."
            )
            .optional(),
        })
        .describe('Ticket sale period.')
        .optional(),
      saleStatus: z
        .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
        .optional(),
      salesDetails: z
        .object({
          unsoldCount: z
            .number()
            .int()
            .describe(
              "Number of tickets that haven't been purchased yet. The field is `null` if the ticket quantity is unlimited."
            )
            .optional()
            .nullable(),
          soldCount: z
            .number()
            .int()
            .describe('Number of purchased tickets.')
            .optional()
            .nullable(),
          reservedCount: z
            .number()
            .int()
            .describe('Number of reserved tickets.')
            .optional()
            .nullable(),
          soldOut: z
            .boolean()
            .describe('Whether the tickets are sold out.')
            .optional()
            .nullable(),
        })
        .describe(
          'Ticket sales information. <br> <br>\n**Note:** This field is only returned when `SALES_DETAILS` is specified in `field` in the request.'
        )
        .optional(),
      limitPerCheckout: z
        .number()
        .int()
        .describe(
          'Number of tickets that can be purchased per checkout. <br> <br>\n**Note:** If the `actualLimit` or `salesDetails.unsoldCount` field value is smaller than `limitPerCheckout`, then it overrides this field.'
        )
        .min(0)
        .max(50)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data extensions.')
        .optional(),
      eventDetails: z
        .object({
          title: z
            .string()
            .describe('Event title.')
            .min(1)
            .max(120)
            .optional()
            .nullable(),
          shortDescription: z
            .string()
            .describe('Short description of the event.')
            .max(500)
            .optional()
            .nullable(),
          location: z
            .object({
              name: z
                .string()
                .describe(
                  'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
                )
                .max(50)
                .optional()
                .nullable(),
              type: z.enum(['VENUE', 'ONLINE']).optional(),
              address: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                  addressLine2: z.string().optional().nullable(),
                })
                .describe('Exact location address.')
                .optional(),
              locationTbd: z
                .boolean()
                .describe('Whether the event location is TBD.')
                .optional()
                .nullable(),
            })
            .describe('Event location.')
            .optional(),
          dateAndTimeSettings: z
            .object({
              dateAndTimeTbd: z
                .boolean()
                .describe('Whether the event date and time are TBD.')
                .optional()
                .nullable(),
              dateAndTimeTbdMessage: z
                .string()
                .describe(
                  'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
                )
                .max(100)
                .optional()
                .nullable(),
              startDate: z
                .date()
                .describe(
                  'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .optional()
                .nullable(),
              timeZoneId: z
                .string()
                .describe(
                  'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .max(100)
                .optional()
                .nullable(),
              hideEndDate: z
                .boolean()
                .describe(
                  'Whether the end date is hidden in the formatted date and time.'
                )
                .optional()
                .nullable(),
              showTimeZone: z
                .boolean()
                .describe(
                  'Whether the time zone is displayed in the formatted schedule.'
                )
                .optional()
                .nullable(),
              recurrenceStatus: z
                .enum([
                  'ONE_TIME',
                  'RECURRING',
                  'RECURRING_UPCOMING',
                  'RECURRING_RECENTLY_ENDED',
                  'RECURRING_RECENTLY_CANCELED',
                ])
                .optional(),
              recurringEvents: z
                .object({
                  individualEventDates: z
                    .array(
                      z.object({
                        startDate: z
                          .date()
                          .describe('Event start date.')
                          .optional()
                          .nullable(),
                        endDate: z
                          .date()
                          .describe('Event end date.')
                          .optional()
                          .nullable(),
                        timeZoneId: z
                          .string()
                          .describe(
                            'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                        showTimeZone: z
                          .boolean()
                          .describe(
                            'Whether the time zone is displayed in a formatted schedule.'
                          )
                          .optional(),
                      })
                    )
                    .max(1000)
                    .optional(),
                  categoryId: z
                    .string()
                    .describe(
                      'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Event repetitions.')
                .optional(),
              formatted: z
                .object({
                  dateAndTime: z
                    .string()
                    .describe(
                      'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  startDate: z
                    .string()
                    .describe(
                      'Formatted start date of the event. Empty for TBD schedules.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  startTime: z
                    .string()
                    .describe(
                      'Formatted start time of the event. Empty for TBD schedules.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  endDate: z
                    .string()
                    .describe(
                      'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  endTime: z
                    .string()
                    .describe(
                      'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                })
                .describe('Formatted date and time settings.')
                .optional(),
            })
            .describe('Event date and time settings.')
            .optional(),
          eventPageUrl: z
            .string()
            .describe('Event page URL components.')
            .optional(),
          status: z
            .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
            .optional(),
        })
        .describe(
          'Information about the even the ticket is for. <br>\n**Note:** This field is only returned when `EVENT_DETAILS` is specified in `field` in the request.'
        )
        .optional(),
      seatingDetails: z
        .object({
          places: z
            .array(
              z.object({
                placeId: z
                  .string()
                  .describe(
                    'Place ID in the format `{sectionId}-{elementId}-{label}`. For example, `0-1-A5`.'
                  )
                  .min(1)
                  .max(20)
                  .optional(),
                label: z
                  .string()
                  .describe(
                    'Human-readable label for this place, such as `A1`, `12`, or `VIP1`.'
                  )
                  .min(1)
                  .max(4)
                  .optional(),
                sectionLabel: z
                  .string()
                  .describe(
                    'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
                  )
                  .min(1)
                  .max(20)
                  .optional()
                  .nullable(),
                elementLabel: z
                  .string()
                  .describe(
                    'Seating element within the section, such as `Row`, `Table`, or `General Admission`.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                availableCapacity: z
                  .number()
                  .int()
                  .describe(
                    'Available capacity at this place.\n\nFor general seating type returns the number of remaining spots.\n\nFor other types returns `1`. For example, if a table has 5 chairs, each chair is treated as a separate place, which has a capacity of 1.'
                  )
                  .min(0)
                  .max(50000)
                  .optional(),
              })
            )
            .max(50000)
            .optional(),
        })
        .describe(
          'Seating information including available seats and areas for this ticket definition. <br>\n**Note:** This field is only returned when `SEATING_DETAILS` is specified in `field` in the request, and when the event has a [seating plan](https://support.wix.com/en/article/wix-events-creating-a-seating-map).'
        )
        .optional(),
    })
    .describe('Ticket definition info.'),
  options: z
    .object({
      fields: z
        .array(z.enum(['SALES_DETAILS', 'EVENT_DETAILS', 'SEATING_DETAILS']))
        .max(5)
        .optional(),
    })
    .describe('Currency info.')
    .optional(),
});
export const CreateTicketDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Ticket definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  eventId: z
    .string()
    .describe('Event ID to which the ticket definition belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the ticket definition is updated. To prevent conflicting changes, the existing `revision` must be used when updating a ticket definition.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ticket definition was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ticket definition was updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Ticket definition name.')
    .min(1)
    .max(30)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe('Ticket definition description.')
    .max(500)
    .optional()
    .nullable(),
  policyText: z
    .string()
    .describe('Ticket definition policy.')
    .max(1000)
    .optional()
    .nullable(),
  hidden: z
    .boolean()
    .describe(
      "Whether this ticket definition is hidden from site visitors and can't be purchased."
    )
    .optional(),
  limited: z
    .boolean()
    .describe('Whether the ticket has a limited maximum quantity.')
    .optional(),
  initialLimit: z
    .number()
    .int()
    .describe(
      'The maximum number of tickets that can be sold for the event when first defining the event. If a seating map is defined after you created a ticket definition, this property is ignored and `actualLimit` is used instead. To create unlimited tickets, skip this field in the request.'
    )
    .optional()
    .nullable(),
  actualLimit: z
    .number()
    .int()
    .describe(
      'The maximum number of tickets that can be sold for the event after adding a seating map to the event. If no seating map is defined, this property is the same as `initialLimit`.'
    )
    .optional()
    .nullable(),
  pricingMethod: z
    .intersection(
      z.object({
        pricingType: z
          .enum(['STANDARD', 'DONATION'])
          .describe('Ticket price type.')
          .optional(),
        free: z
          .boolean()
          .describe(
            'Whether the ticket is free. To create a free ticket, enter `"0"` in `pricingMethod.fixedPrice.value`.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          fixedPrice: z.never().optional(),
          guestPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
        }),
        z.object({
          guestPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
          fixedPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                )
                .optional(),
            })
            .describe('Same ticket price for everyone.'),
        }),
        z.object({
          fixedPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
          guestPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                )
                .optional(),
            })
            .describe(
              'Guests choose how much they\'d like to pay for the ticket. You can set the minimum price, or specify `"0"` in the request to make the ticket free. The price can be updated to a higher amount by a guest during the checkout.'
            ),
        }),
        z.object({
          fixedPrice: z.never().optional(),
          guestPrice: z.never().optional(),
          pricingOptions: z
            .object({
              optionDetails: z
                .array(
                  z.object({
                    optionId: z
                      .string()
                      .describe('Ticket price option ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe(
                        'Ticket price option name, such as "Child Ticket".'
                      )
                      .min(1)
                      .max(200)
                      .optional()
                      .nullable(),
                    price: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                          )
                          .optional(),
                      })
                      .describe('Ticket price.')
                      .optional(),
                  })
                )
                .max(100)
                .optional(),
            })
            .describe(
              'Sets of various ticket prices. For example, you can charge different prices for children and adults.'
            ),
        }),
      ])
    )
    .describe('Ticket pricing method.')
    .optional(),
  feeType: z
    .enum(['FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT', 'NO_FEE'])
    .describe('Type of ticket service fee to collect.')
    .optional(),
  salePeriod: z
    .object({
      startDate: z
        .date()
        .describe(
          'Date and time the ticket sale starts. Rounded down to the nearest minute.'
        )
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Date and time the ticket sale ends. Rounded down to the nearest minute.'
        )
        .optional()
        .nullable(),
      displayNotOnSale: z
        .boolean()
        .describe("Whether to display the ticket if it's not available to buy.")
        .optional(),
    })
    .describe('Ticket sale period.')
    .optional(),
  saleStatus: z
    .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
    .describe('Ticket sale status.')
    .optional(),
  salesDetails: z
    .object({
      unsoldCount: z
        .number()
        .int()
        .describe(
          "Number of tickets that haven't been purchased yet. The field is `null` if the ticket quantity is unlimited."
        )
        .optional()
        .nullable(),
      soldCount: z
        .number()
        .int()
        .describe('Number of purchased tickets.')
        .optional()
        .nullable(),
      reservedCount: z
        .number()
        .int()
        .describe('Number of reserved tickets.')
        .optional()
        .nullable(),
      soldOut: z
        .boolean()
        .describe('Whether the tickets are sold out.')
        .optional()
        .nullable(),
    })
    .describe(
      'Ticket sales information. <br> <br>\n**Note:** This field is only returned when `SALES_DETAILS` is specified in `field` in the request.'
    )
    .optional(),
  limitPerCheckout: z
    .number()
    .int()
    .describe(
      'Number of tickets that can be purchased per checkout. <br> <br>\n**Note:** If the `actualLimit` or `salesDetails.unsoldCount` field value is smaller than `limitPerCheckout`, then it overrides this field.'
    )
    .min(0)
    .max(50)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data extensions.')
    .optional(),
  eventDetails: z
    .object({
      title: z
        .string()
        .describe('Event title.')
        .min(1)
        .max(120)
        .optional()
        .nullable(),
      shortDescription: z
        .string()
        .describe('Short description of the event.')
        .max(500)
        .optional()
        .nullable(),
      location: z
        .object({
          name: z
            .string()
            .describe(
              'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
            )
            .max(50)
            .optional()
            .nullable(),
          type: z
            .enum(['VENUE', 'ONLINE'])
            .describe('Location type.')
            .optional(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Exact location address.')
            .optional(),
          locationTbd: z
            .boolean()
            .describe('Whether the event location is TBD.')
            .optional()
            .nullable(),
        })
        .describe('Event location.')
        .optional(),
      dateAndTimeSettings: z
        .object({
          dateAndTimeTbd: z
            .boolean()
            .describe('Whether the event date and time are TBD.')
            .optional()
            .nullable(),
          dateAndTimeTbdMessage: z
            .string()
            .describe(
              'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
            )
            .max(100)
            .optional()
            .nullable(),
          startDate: z
            .date()
            .describe(
              'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          timeZoneId: z
            .string()
            .describe(
              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .max(100)
            .optional()
            .nullable(),
          hideEndDate: z
            .boolean()
            .describe(
              'Whether the end date is hidden in the formatted date and time.'
            )
            .optional()
            .nullable(),
          showTimeZone: z
            .boolean()
            .describe(
              'Whether the time zone is displayed in the formatted schedule.'
            )
            .optional()
            .nullable(),
          recurrenceStatus: z
            .enum([
              'ONE_TIME',
              'RECURRING',
              'RECURRING_UPCOMING',
              'RECURRING_RECENTLY_ENDED',
              'RECURRING_RECENTLY_CANCELED',
            ])
            .describe('Repeating event status.')
            .optional(),
          recurringEvents: z
            .object({
              individualEventDates: z
                .array(
                  z.object({
                    startDate: z
                      .date()
                      .describe('Event start date.')
                      .optional()
                      .nullable(),
                    endDate: z
                      .date()
                      .describe('Event end date.')
                      .optional()
                      .nullable(),
                    timeZoneId: z
                      .string()
                      .describe(
                        'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    showTimeZone: z
                      .boolean()
                      .describe(
                        'Whether the time zone is displayed in a formatted schedule.'
                      )
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
              categoryId: z
                .string()
                .describe(
                  'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Event repetitions.')
            .optional(),
          formatted: z
            .object({
              dateAndTime: z
                .string()
                .describe(
                  'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                )
                .max(500)
                .optional()
                .nullable(),
              startDate: z
                .string()
                .describe(
                  'Formatted start date of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              startTime: z
                .string()
                .describe(
                  'Formatted start time of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              endDate: z
                .string()
                .describe(
                  'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
              endTime: z
                .string()
                .describe(
                  'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
            })
            .describe('Formatted date and time settings.')
            .optional(),
        })
        .describe('Event date and time settings.')
        .optional(),
      eventPageUrl: z
        .string()
        .describe('Event page URL components.')
        .optional(),
      status: z
        .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
        .describe('Event status.')
        .optional(),
    })
    .describe(
      'Information about the even the ticket is for. <br>\n**Note:** This field is only returned when `EVENT_DETAILS` is specified in `field` in the request.'
    )
    .optional(),
  seatingDetails: z
    .object({
      places: z
        .array(
          z.object({
            placeId: z
              .string()
              .describe(
                'Place ID in the format `{sectionId}-{elementId}-{label}`. For example, `0-1-A5`.'
              )
              .min(1)
              .max(20)
              .optional(),
            label: z
              .string()
              .describe(
                'Human-readable label for this place, such as `A1`, `12`, or `VIP1`.'
              )
              .min(1)
              .max(4)
              .optional(),
            sectionLabel: z
              .string()
              .describe(
                'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
              )
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            elementLabel: z
              .string()
              .describe(
                'Seating element within the section, such as `Row`, `Table`, or `General Admission`.'
              )
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            availableCapacity: z
              .number()
              .int()
              .describe(
                'Available capacity at this place.\n\nFor general seating type returns the number of remaining spots.\n\nFor other types returns `1`. For example, if a table has 5 chairs, each chair is treated as a separate place, which has a capacity of 1.'
              )
              .min(0)
              .max(50000)
              .optional(),
          })
        )
        .max(50000)
        .optional(),
    })
    .describe(
      'Seating information including available seats and areas for this ticket definition. <br>\n**Note:** This field is only returned when `SEATING_DETAILS` is specified in `field` in the request, and when the event has a [seating plan](https://support.wix.com/en/article/wix-events-creating-a-seating-map).'
    )
    .optional(),
});
export const UpdateTicketDefinitionRequest = z.object({
  _id: z
    .string()
    .describe('Ticket definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  ticketDefinition: z
    .object({
      _id: z
        .string()
        .describe('Ticket definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      eventId: z
        .string()
        .describe('Event ID to which the ticket definition belongs.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ticket definition is updated. To prevent conflicting changes, the existing `revision` must be used when updating a ticket definition.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the ticket definition was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ticket definition was updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Ticket definition name.')
        .min(1)
        .max(30)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe('Ticket definition description.')
        .max(500)
        .optional()
        .nullable(),
      policyText: z
        .string()
        .describe('Ticket definition policy.')
        .max(1000)
        .optional()
        .nullable(),
      hidden: z
        .boolean()
        .describe(
          "Whether this ticket definition is hidden from site visitors and can't be purchased."
        )
        .optional(),
      limited: z
        .boolean()
        .describe('Whether the ticket has a limited maximum quantity.')
        .optional(),
      initialLimit: z
        .number()
        .int()
        .describe(
          'The maximum number of tickets that can be sold for the event when first defining the event. If a seating map is defined after you created a ticket definition, this property is ignored and `actualLimit` is used instead. To create unlimited tickets, skip this field in the request.'
        )
        .optional()
        .nullable(),
      actualLimit: z
        .number()
        .int()
        .describe(
          'The maximum number of tickets that can be sold for the event after adding a seating map to the event. If no seating map is defined, this property is the same as `initialLimit`.'
        )
        .optional()
        .nullable(),
      pricingMethod: z
        .intersection(
          z.object({
            pricingType: z.enum(['STANDARD', 'DONATION']).optional(),
            free: z
              .boolean()
              .describe(
                'Whether the ticket is free. To create a free ticket, enter `"0"` in `pricingMethod.fixedPrice.value`.'
              )
              .optional(),
          }),
          z.xor([
            z.object({
              fixedPrice: z.never().optional(),
              guestPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
            }),
            z.object({
              guestPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
              fixedPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                    )
                    .optional(),
                })
                .describe('Same ticket price for everyone.'),
            }),
            z.object({
              fixedPrice: z.never().optional(),
              pricingOptions: z.never().optional(),
              guestPrice: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                    )
                    .optional(),
                })
                .describe(
                  'Guests choose how much they\'d like to pay for the ticket. You can set the minimum price, or specify `"0"` in the request to make the ticket free. The price can be updated to a higher amount by a guest during the checkout.'
                ),
            }),
            z.object({
              fixedPrice: z.never().optional(),
              guestPrice: z.never().optional(),
              pricingOptions: z
                .object({
                  optionDetails: z
                    .array(
                      z.object({
                        optionId: z
                          .string()
                          .describe('Ticket price option ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe(
                            'Ticket price option name, such as "Child Ticket".'
                          )
                          .min(1)
                          .max(200)
                          .optional()
                          .nullable(),
                        price: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                              )
                              .optional(),
                            currency: z
                              .string()
                              .describe(
                                'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                              )
                              .optional(),
                          })
                          .describe('Ticket price.')
                          .optional(),
                      })
                    )
                    .max(100)
                    .optional(),
                })
                .describe(
                  'Sets of various ticket prices. For example, you can charge different prices for children and adults.'
                ),
            }),
          ])
        )
        .describe('Ticket pricing method.')
        .optional(),
      feeType: z
        .enum(['FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT', 'NO_FEE'])
        .optional(),
      salePeriod: z
        .object({
          startDate: z
            .date()
            .describe(
              'Date and time the ticket sale starts. Rounded down to the nearest minute.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Date and time the ticket sale ends. Rounded down to the nearest minute.'
            )
            .optional()
            .nullable(),
          displayNotOnSale: z
            .boolean()
            .describe(
              "Whether to display the ticket if it's not available to buy."
            )
            .optional(),
        })
        .describe('Ticket sale period.')
        .optional(),
      saleStatus: z
        .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
        .optional(),
      salesDetails: z
        .object({
          unsoldCount: z
            .number()
            .int()
            .describe(
              "Number of tickets that haven't been purchased yet. The field is `null` if the ticket quantity is unlimited."
            )
            .optional()
            .nullable(),
          soldCount: z
            .number()
            .int()
            .describe('Number of purchased tickets.')
            .optional()
            .nullable(),
          reservedCount: z
            .number()
            .int()
            .describe('Number of reserved tickets.')
            .optional()
            .nullable(),
          soldOut: z
            .boolean()
            .describe('Whether the tickets are sold out.')
            .optional()
            .nullable(),
        })
        .describe(
          'Ticket sales information. <br> <br>\n**Note:** This field is only returned when `SALES_DETAILS` is specified in `field` in the request.'
        )
        .optional(),
      limitPerCheckout: z
        .number()
        .int()
        .describe(
          'Number of tickets that can be purchased per checkout. <br> <br>\n**Note:** If the `actualLimit` or `salesDetails.unsoldCount` field value is smaller than `limitPerCheckout`, then it overrides this field.'
        )
        .min(0)
        .max(50)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data extensions.')
        .optional(),
      eventDetails: z
        .object({
          title: z
            .string()
            .describe('Event title.')
            .min(1)
            .max(120)
            .optional()
            .nullable(),
          shortDescription: z
            .string()
            .describe('Short description of the event.')
            .max(500)
            .optional()
            .nullable(),
          location: z
            .object({
              name: z
                .string()
                .describe(
                  'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
                )
                .max(50)
                .optional()
                .nullable(),
              type: z.enum(['VENUE', 'ONLINE']).optional(),
              address: z
                .object({
                  city: z.string().optional().nullable(),
                  subdivision: z.string().optional().nullable(),
                  country: z.string().optional().nullable(),
                  postalCode: z.string().optional().nullable(),
                  addressLine1: z.string().optional().nullable(),
                  addressLine2: z.string().optional().nullable(),
                })
                .describe('Exact location address.')
                .optional(),
              locationTbd: z
                .boolean()
                .describe('Whether the event location is TBD.')
                .optional()
                .nullable(),
            })
            .describe('Event location.')
            .optional(),
          dateAndTimeSettings: z
            .object({
              dateAndTimeTbd: z
                .boolean()
                .describe('Whether the event date and time are TBD.')
                .optional()
                .nullable(),
              dateAndTimeTbdMessage: z
                .string()
                .describe(
                  'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
                )
                .max(100)
                .optional()
                .nullable(),
              startDate: z
                .date()
                .describe(
                  'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .optional()
                .nullable(),
              endDate: z
                .date()
                .describe(
                  'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .optional()
                .nullable(),
              timeZoneId: z
                .string()
                .describe(
                  'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                )
                .max(100)
                .optional()
                .nullable(),
              hideEndDate: z
                .boolean()
                .describe(
                  'Whether the end date is hidden in the formatted date and time.'
                )
                .optional()
                .nullable(),
              showTimeZone: z
                .boolean()
                .describe(
                  'Whether the time zone is displayed in the formatted schedule.'
                )
                .optional()
                .nullable(),
              recurrenceStatus: z
                .enum([
                  'ONE_TIME',
                  'RECURRING',
                  'RECURRING_UPCOMING',
                  'RECURRING_RECENTLY_ENDED',
                  'RECURRING_RECENTLY_CANCELED',
                ])
                .optional(),
              recurringEvents: z
                .object({
                  individualEventDates: z
                    .array(
                      z.object({
                        startDate: z
                          .date()
                          .describe('Event start date.')
                          .optional()
                          .nullable(),
                        endDate: z
                          .date()
                          .describe('Event end date.')
                          .optional()
                          .nullable(),
                        timeZoneId: z
                          .string()
                          .describe(
                            'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                        showTimeZone: z
                          .boolean()
                          .describe(
                            'Whether the time zone is displayed in a formatted schedule.'
                          )
                          .optional(),
                      })
                    )
                    .max(1000)
                    .optional(),
                  categoryId: z
                    .string()
                    .describe(
                      'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Event repetitions.')
                .optional(),
              formatted: z
                .object({
                  dateAndTime: z
                    .string()
                    .describe(
                      'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  startDate: z
                    .string()
                    .describe(
                      'Formatted start date of the event. Empty for TBD schedules.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  startTime: z
                    .string()
                    .describe(
                      'Formatted start time of the event. Empty for TBD schedules.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  endDate: z
                    .string()
                    .describe(
                      'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                  endTime: z
                    .string()
                    .describe(
                      'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                    )
                    .max(500)
                    .optional()
                    .nullable(),
                })
                .describe('Formatted date and time settings.')
                .optional(),
            })
            .describe('Event date and time settings.')
            .optional(),
          eventPageUrl: z
            .string()
            .describe('Event page URL components.')
            .optional(),
          status: z
            .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
            .optional(),
        })
        .describe(
          'Information about the even the ticket is for. <br>\n**Note:** This field is only returned when `EVENT_DETAILS` is specified in `field` in the request.'
        )
        .optional(),
      seatingDetails: z
        .object({
          places: z
            .array(
              z.object({
                placeId: z
                  .string()
                  .describe(
                    'Place ID in the format `{sectionId}-{elementId}-{label}`. For example, `0-1-A5`.'
                  )
                  .min(1)
                  .max(20)
                  .optional(),
                label: z
                  .string()
                  .describe(
                    'Human-readable label for this place, such as `A1`, `12`, or `VIP1`.'
                  )
                  .min(1)
                  .max(4)
                  .optional(),
                sectionLabel: z
                  .string()
                  .describe(
                    'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
                  )
                  .min(1)
                  .max(20)
                  .optional()
                  .nullable(),
                elementLabel: z
                  .string()
                  .describe(
                    'Seating element within the section, such as `Row`, `Table`, or `General Admission`.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                availableCapacity: z
                  .number()
                  .int()
                  .describe(
                    'Available capacity at this place.\n\nFor general seating type returns the number of remaining spots.\n\nFor other types returns `1`. For example, if a table has 5 chairs, each chair is treated as a separate place, which has a capacity of 1.'
                  )
                  .min(0)
                  .max(50000)
                  .optional(),
              })
            )
            .max(50000)
            .optional(),
        })
        .describe(
          'Seating information including available seats and areas for this ticket definition. <br>\n**Note:** This field is only returned when `SEATING_DETAILS` is specified in `field` in the request, and when the event has a [seating plan](https://support.wix.com/en/article/wix-events-creating-a-seating-map).'
        )
        .optional(),
    })
    .describe('Ticket definition to update.'),
  options: z
    .object({
      fields: z
        .array(z.enum(['SALES_DETAILS', 'EVENT_DETAILS', 'SEATING_DETAILS']))
        .max(5)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const UpdateTicketDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Ticket definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  eventId: z
    .string()
    .describe('Event ID to which the ticket definition belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the ticket definition is updated. To prevent conflicting changes, the existing `revision` must be used when updating a ticket definition.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ticket definition was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ticket definition was updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Ticket definition name.')
    .min(1)
    .max(30)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe('Ticket definition description.')
    .max(500)
    .optional()
    .nullable(),
  policyText: z
    .string()
    .describe('Ticket definition policy.')
    .max(1000)
    .optional()
    .nullable(),
  hidden: z
    .boolean()
    .describe(
      "Whether this ticket definition is hidden from site visitors and can't be purchased."
    )
    .optional(),
  limited: z
    .boolean()
    .describe('Whether the ticket has a limited maximum quantity.')
    .optional(),
  initialLimit: z
    .number()
    .int()
    .describe(
      'The maximum number of tickets that can be sold for the event when first defining the event. If a seating map is defined after you created a ticket definition, this property is ignored and `actualLimit` is used instead. To create unlimited tickets, skip this field in the request.'
    )
    .optional()
    .nullable(),
  actualLimit: z
    .number()
    .int()
    .describe(
      'The maximum number of tickets that can be sold for the event after adding a seating map to the event. If no seating map is defined, this property is the same as `initialLimit`.'
    )
    .optional()
    .nullable(),
  pricingMethod: z
    .intersection(
      z.object({
        pricingType: z
          .enum(['STANDARD', 'DONATION'])
          .describe('Ticket price type.')
          .optional(),
        free: z
          .boolean()
          .describe(
            'Whether the ticket is free. To create a free ticket, enter `"0"` in `pricingMethod.fixedPrice.value`.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          fixedPrice: z.never().optional(),
          guestPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
        }),
        z.object({
          guestPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
          fixedPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                )
                .optional(),
            })
            .describe('Same ticket price for everyone.'),
        }),
        z.object({
          fixedPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
          guestPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                )
                .optional(),
            })
            .describe(
              'Guests choose how much they\'d like to pay for the ticket. You can set the minimum price, or specify `"0"` in the request to make the ticket free. The price can be updated to a higher amount by a guest during the checkout.'
            ),
        }),
        z.object({
          fixedPrice: z.never().optional(),
          guestPrice: z.never().optional(),
          pricingOptions: z
            .object({
              optionDetails: z
                .array(
                  z.object({
                    optionId: z
                      .string()
                      .describe('Ticket price option ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe(
                        'Ticket price option name, such as "Child Ticket".'
                      )
                      .min(1)
                      .max(200)
                      .optional()
                      .nullable(),
                    price: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                          )
                          .optional(),
                      })
                      .describe('Ticket price.')
                      .optional(),
                  })
                )
                .max(100)
                .optional(),
            })
            .describe(
              'Sets of various ticket prices. For example, you can charge different prices for children and adults.'
            ),
        }),
      ])
    )
    .describe('Ticket pricing method.')
    .optional(),
  feeType: z
    .enum(['FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT', 'NO_FEE'])
    .describe('Type of ticket service fee to collect.')
    .optional(),
  salePeriod: z
    .object({
      startDate: z
        .date()
        .describe(
          'Date and time the ticket sale starts. Rounded down to the nearest minute.'
        )
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Date and time the ticket sale ends. Rounded down to the nearest minute.'
        )
        .optional()
        .nullable(),
      displayNotOnSale: z
        .boolean()
        .describe("Whether to display the ticket if it's not available to buy.")
        .optional(),
    })
    .describe('Ticket sale period.')
    .optional(),
  saleStatus: z
    .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
    .describe('Ticket sale status.')
    .optional(),
  salesDetails: z
    .object({
      unsoldCount: z
        .number()
        .int()
        .describe(
          "Number of tickets that haven't been purchased yet. The field is `null` if the ticket quantity is unlimited."
        )
        .optional()
        .nullable(),
      soldCount: z
        .number()
        .int()
        .describe('Number of purchased tickets.')
        .optional()
        .nullable(),
      reservedCount: z
        .number()
        .int()
        .describe('Number of reserved tickets.')
        .optional()
        .nullable(),
      soldOut: z
        .boolean()
        .describe('Whether the tickets are sold out.')
        .optional()
        .nullable(),
    })
    .describe(
      'Ticket sales information. <br> <br>\n**Note:** This field is only returned when `SALES_DETAILS` is specified in `field` in the request.'
    )
    .optional(),
  limitPerCheckout: z
    .number()
    .int()
    .describe(
      'Number of tickets that can be purchased per checkout. <br> <br>\n**Note:** If the `actualLimit` or `salesDetails.unsoldCount` field value is smaller than `limitPerCheckout`, then it overrides this field.'
    )
    .min(0)
    .max(50)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data extensions.')
    .optional(),
  eventDetails: z
    .object({
      title: z
        .string()
        .describe('Event title.')
        .min(1)
        .max(120)
        .optional()
        .nullable(),
      shortDescription: z
        .string()
        .describe('Short description of the event.')
        .max(500)
        .optional()
        .nullable(),
      location: z
        .object({
          name: z
            .string()
            .describe(
              'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
            )
            .max(50)
            .optional()
            .nullable(),
          type: z
            .enum(['VENUE', 'ONLINE'])
            .describe('Location type.')
            .optional(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Exact location address.')
            .optional(),
          locationTbd: z
            .boolean()
            .describe('Whether the event location is TBD.')
            .optional()
            .nullable(),
        })
        .describe('Event location.')
        .optional(),
      dateAndTimeSettings: z
        .object({
          dateAndTimeTbd: z
            .boolean()
            .describe('Whether the event date and time are TBD.')
            .optional()
            .nullable(),
          dateAndTimeTbdMessage: z
            .string()
            .describe(
              'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
            )
            .max(100)
            .optional()
            .nullable(),
          startDate: z
            .date()
            .describe(
              'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          timeZoneId: z
            .string()
            .describe(
              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .max(100)
            .optional()
            .nullable(),
          hideEndDate: z
            .boolean()
            .describe(
              'Whether the end date is hidden in the formatted date and time.'
            )
            .optional()
            .nullable(),
          showTimeZone: z
            .boolean()
            .describe(
              'Whether the time zone is displayed in the formatted schedule.'
            )
            .optional()
            .nullable(),
          recurrenceStatus: z
            .enum([
              'ONE_TIME',
              'RECURRING',
              'RECURRING_UPCOMING',
              'RECURRING_RECENTLY_ENDED',
              'RECURRING_RECENTLY_CANCELED',
            ])
            .describe('Repeating event status.')
            .optional(),
          recurringEvents: z
            .object({
              individualEventDates: z
                .array(
                  z.object({
                    startDate: z
                      .date()
                      .describe('Event start date.')
                      .optional()
                      .nullable(),
                    endDate: z
                      .date()
                      .describe('Event end date.')
                      .optional()
                      .nullable(),
                    timeZoneId: z
                      .string()
                      .describe(
                        'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    showTimeZone: z
                      .boolean()
                      .describe(
                        'Whether the time zone is displayed in a formatted schedule.'
                      )
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
              categoryId: z
                .string()
                .describe(
                  'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Event repetitions.')
            .optional(),
          formatted: z
            .object({
              dateAndTime: z
                .string()
                .describe(
                  'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                )
                .max(500)
                .optional()
                .nullable(),
              startDate: z
                .string()
                .describe(
                  'Formatted start date of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              startTime: z
                .string()
                .describe(
                  'Formatted start time of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              endDate: z
                .string()
                .describe(
                  'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
              endTime: z
                .string()
                .describe(
                  'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
            })
            .describe('Formatted date and time settings.')
            .optional(),
        })
        .describe('Event date and time settings.')
        .optional(),
      eventPageUrl: z
        .string()
        .describe('Event page URL components.')
        .optional(),
      status: z
        .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
        .describe('Event status.')
        .optional(),
    })
    .describe(
      'Information about the even the ticket is for. <br>\n**Note:** This field is only returned when `EVENT_DETAILS` is specified in `field` in the request.'
    )
    .optional(),
  seatingDetails: z
    .object({
      places: z
        .array(
          z.object({
            placeId: z
              .string()
              .describe(
                'Place ID in the format `{sectionId}-{elementId}-{label}`. For example, `0-1-A5`.'
              )
              .min(1)
              .max(20)
              .optional(),
            label: z
              .string()
              .describe(
                'Human-readable label for this place, such as `A1`, `12`, or `VIP1`.'
              )
              .min(1)
              .max(4)
              .optional(),
            sectionLabel: z
              .string()
              .describe(
                'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
              )
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            elementLabel: z
              .string()
              .describe(
                'Seating element within the section, such as `Row`, `Table`, or `General Admission`.'
              )
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            availableCapacity: z
              .number()
              .int()
              .describe(
                'Available capacity at this place.\n\nFor general seating type returns the number of remaining spots.\n\nFor other types returns `1`. For example, if a table has 5 chairs, each chair is treated as a separate place, which has a capacity of 1.'
              )
              .min(0)
              .max(50000)
              .optional(),
          })
        )
        .max(50000)
        .optional(),
    })
    .describe(
      'Seating information including available seats and areas for this ticket definition. <br>\n**Note:** This field is only returned when `SEATING_DETAILS` is specified in `field` in the request, and when the event has a [seating plan](https://support.wix.com/en/article/wix-events-creating-a-seating-map).'
    )
    .optional(),
});
export const GetTicketDefinitionRequest = z.object({
  ticketDefinitionId: z
    .string()
    .describe('Ticket definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['SALES_DETAILS', 'EVENT_DETAILS', 'SEATING_DETAILS']))
        .max(5)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const GetTicketDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Ticket definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  eventId: z
    .string()
    .describe('Event ID to which the ticket definition belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the ticket definition is updated. To prevent conflicting changes, the existing `revision` must be used when updating a ticket definition.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ticket definition was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ticket definition was updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Ticket definition name.')
    .min(1)
    .max(30)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe('Ticket definition description.')
    .max(500)
    .optional()
    .nullable(),
  policyText: z
    .string()
    .describe('Ticket definition policy.')
    .max(1000)
    .optional()
    .nullable(),
  hidden: z
    .boolean()
    .describe(
      "Whether this ticket definition is hidden from site visitors and can't be purchased."
    )
    .optional(),
  limited: z
    .boolean()
    .describe('Whether the ticket has a limited maximum quantity.')
    .optional(),
  initialLimit: z
    .number()
    .int()
    .describe(
      'The maximum number of tickets that can be sold for the event when first defining the event. If a seating map is defined after you created a ticket definition, this property is ignored and `actualLimit` is used instead. To create unlimited tickets, skip this field in the request.'
    )
    .optional()
    .nullable(),
  actualLimit: z
    .number()
    .int()
    .describe(
      'The maximum number of tickets that can be sold for the event after adding a seating map to the event. If no seating map is defined, this property is the same as `initialLimit`.'
    )
    .optional()
    .nullable(),
  pricingMethod: z
    .intersection(
      z.object({
        pricingType: z
          .enum(['STANDARD', 'DONATION'])
          .describe('Ticket price type.')
          .optional(),
        free: z
          .boolean()
          .describe(
            'Whether the ticket is free. To create a free ticket, enter `"0"` in `pricingMethod.fixedPrice.value`.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          fixedPrice: z.never().optional(),
          guestPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
        }),
        z.object({
          guestPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
          fixedPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                )
                .optional(),
            })
            .describe('Same ticket price for everyone.'),
        }),
        z.object({
          fixedPrice: z.never().optional(),
          pricingOptions: z.never().optional(),
          guestPrice: z
            .object({
              value: z
                .string()
                .describe(
                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                )
                .optional(),
            })
            .describe(
              'Guests choose how much they\'d like to pay for the ticket. You can set the minimum price, or specify `"0"` in the request to make the ticket free. The price can be updated to a higher amount by a guest during the checkout.'
            ),
        }),
        z.object({
          fixedPrice: z.never().optional(),
          guestPrice: z.never().optional(),
          pricingOptions: z
            .object({
              optionDetails: z
                .array(
                  z.object({
                    optionId: z
                      .string()
                      .describe('Ticket price option ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    name: z
                      .string()
                      .describe(
                        'Ticket price option name, such as "Child Ticket".'
                      )
                      .min(1)
                      .max(200)
                      .optional()
                      .nullable(),
                    price: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                          )
                          .optional(),
                        currency: z
                          .string()
                          .describe(
                            'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                          )
                          .optional(),
                      })
                      .describe('Ticket price.')
                      .optional(),
                  })
                )
                .max(100)
                .optional(),
            })
            .describe(
              'Sets of various ticket prices. For example, you can charge different prices for children and adults.'
            ),
        }),
      ])
    )
    .describe('Ticket pricing method.')
    .optional(),
  feeType: z
    .enum(['FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT', 'NO_FEE'])
    .describe('Type of ticket service fee to collect.')
    .optional(),
  salePeriod: z
    .object({
      startDate: z
        .date()
        .describe(
          'Date and time the ticket sale starts. Rounded down to the nearest minute.'
        )
        .optional()
        .nullable(),
      endDate: z
        .date()
        .describe(
          'Date and time the ticket sale ends. Rounded down to the nearest minute.'
        )
        .optional()
        .nullable(),
      displayNotOnSale: z
        .boolean()
        .describe("Whether to display the ticket if it's not available to buy.")
        .optional(),
    })
    .describe('Ticket sale period.')
    .optional(),
  saleStatus: z
    .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
    .describe('Ticket sale status.')
    .optional(),
  salesDetails: z
    .object({
      unsoldCount: z
        .number()
        .int()
        .describe(
          "Number of tickets that haven't been purchased yet. The field is `null` if the ticket quantity is unlimited."
        )
        .optional()
        .nullable(),
      soldCount: z
        .number()
        .int()
        .describe('Number of purchased tickets.')
        .optional()
        .nullable(),
      reservedCount: z
        .number()
        .int()
        .describe('Number of reserved tickets.')
        .optional()
        .nullable(),
      soldOut: z
        .boolean()
        .describe('Whether the tickets are sold out.')
        .optional()
        .nullable(),
    })
    .describe(
      'Ticket sales information. <br> <br>\n**Note:** This field is only returned when `SALES_DETAILS` is specified in `field` in the request.'
    )
    .optional(),
  limitPerCheckout: z
    .number()
    .int()
    .describe(
      'Number of tickets that can be purchased per checkout. <br> <br>\n**Note:** If the `actualLimit` or `salesDetails.unsoldCount` field value is smaller than `limitPerCheckout`, then it overrides this field.'
    )
    .min(0)
    .max(50)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data extensions.')
    .optional(),
  eventDetails: z
    .object({
      title: z
        .string()
        .describe('Event title.')
        .min(1)
        .max(120)
        .optional()
        .nullable(),
      shortDescription: z
        .string()
        .describe('Short description of the event.')
        .max(500)
        .optional()
        .nullable(),
      location: z
        .object({
          name: z
            .string()
            .describe(
              'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
            )
            .max(50)
            .optional()
            .nullable(),
          type: z
            .enum(['VENUE', 'ONLINE'])
            .describe('Location type.')
            .optional(),
          address: z
            .object({
              city: z.string().optional().nullable(),
              subdivision: z.string().optional().nullable(),
              country: z.string().optional().nullable(),
              postalCode: z.string().optional().nullable(),
              addressLine1: z.string().optional().nullable(),
              addressLine2: z.string().optional().nullable(),
            })
            .describe('Exact location address.')
            .optional(),
          locationTbd: z
            .boolean()
            .describe('Whether the event location is TBD.')
            .optional()
            .nullable(),
        })
        .describe('Event location.')
        .optional(),
      dateAndTimeSettings: z
        .object({
          dateAndTimeTbd: z
            .boolean()
            .describe('Whether the event date and time are TBD.')
            .optional()
            .nullable(),
          dateAndTimeTbdMessage: z
            .string()
            .describe(
              'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
            )
            .max(100)
            .optional()
            .nullable(),
          startDate: z
            .date()
            .describe(
              'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          endDate: z
            .date()
            .describe(
              'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .optional()
            .nullable(),
          timeZoneId: z
            .string()
            .describe(
              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
            )
            .max(100)
            .optional()
            .nullable(),
          hideEndDate: z
            .boolean()
            .describe(
              'Whether the end date is hidden in the formatted date and time.'
            )
            .optional()
            .nullable(),
          showTimeZone: z
            .boolean()
            .describe(
              'Whether the time zone is displayed in the formatted schedule.'
            )
            .optional()
            .nullable(),
          recurrenceStatus: z
            .enum([
              'ONE_TIME',
              'RECURRING',
              'RECURRING_UPCOMING',
              'RECURRING_RECENTLY_ENDED',
              'RECURRING_RECENTLY_CANCELED',
            ])
            .describe('Repeating event status.')
            .optional(),
          recurringEvents: z
            .object({
              individualEventDates: z
                .array(
                  z.object({
                    startDate: z
                      .date()
                      .describe('Event start date.')
                      .optional()
                      .nullable(),
                    endDate: z
                      .date()
                      .describe('Event end date.')
                      .optional()
                      .nullable(),
                    timeZoneId: z
                      .string()
                      .describe(
                        'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    showTimeZone: z
                      .boolean()
                      .describe(
                        'Whether the time zone is displayed in a formatted schedule.'
                      )
                      .optional(),
                  })
                )
                .max(1000)
                .optional(),
              categoryId: z
                .string()
                .describe(
                  'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                )
                .max(100)
                .optional()
                .nullable(),
            })
            .describe('Event repetitions.')
            .optional(),
          formatted: z
            .object({
              dateAndTime: z
                .string()
                .describe(
                  'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                )
                .max(500)
                .optional()
                .nullable(),
              startDate: z
                .string()
                .describe(
                  'Formatted start date of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              startTime: z
                .string()
                .describe(
                  'Formatted start time of the event. Empty for TBD schedules.'
                )
                .max(500)
                .optional()
                .nullable(),
              endDate: z
                .string()
                .describe(
                  'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
              endTime: z
                .string()
                .describe(
                  'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                )
                .max(500)
                .optional()
                .nullable(),
            })
            .describe('Formatted date and time settings.')
            .optional(),
        })
        .describe('Event date and time settings.')
        .optional(),
      eventPageUrl: z
        .string()
        .describe('Event page URL components.')
        .optional(),
      status: z
        .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
        .describe('Event status.')
        .optional(),
    })
    .describe(
      'Information about the even the ticket is for. <br>\n**Note:** This field is only returned when `EVENT_DETAILS` is specified in `field` in the request.'
    )
    .optional(),
  seatingDetails: z
    .object({
      places: z
        .array(
          z.object({
            placeId: z
              .string()
              .describe(
                'Place ID in the format `{sectionId}-{elementId}-{label}`. For example, `0-1-A5`.'
              )
              .min(1)
              .max(20)
              .optional(),
            label: z
              .string()
              .describe(
                'Human-readable label for this place, such as `A1`, `12`, or `VIP1`.'
              )
              .min(1)
              .max(4)
              .optional(),
            sectionLabel: z
              .string()
              .describe(
                'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
              )
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            elementLabel: z
              .string()
              .describe(
                'Seating element within the section, such as `Row`, `Table`, or `General Admission`.'
              )
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            availableCapacity: z
              .number()
              .int()
              .describe(
                'Available capacity at this place.\n\nFor general seating type returns the number of remaining spots.\n\nFor other types returns `1`. For example, if a table has 5 chairs, each chair is treated as a separate place, which has a capacity of 1.'
              )
              .min(0)
              .max(50000)
              .optional(),
          })
        )
        .max(50000)
        .optional(),
    })
    .describe(
      'Seating information including available seats and areas for this ticket definition. <br>\n**Note:** This field is only returned when `SEATING_DETAILS` is specified in `field` in the request, and when the event has a [seating plan](https://support.wix.com/en/article/wix-events-creating-a-seating-map).'
    )
    .optional(),
});
export const DeleteTicketDefinitionRequest = z.object({
  ticketDefinitionId: z
    .string()
    .describe('ID of the ticket definition to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteTicketDefinitionResponse = z.object({});
export const ReorderTicketDefinitionsRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .intersection(
      z.object({
        ticketDefinitionId: z
          .string()
          .describe('Ticket definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
      }),
      z.xor([
        z.object({
          beforeDefinitionId: z.never().optional(),
          afterDefinitionId: z.never().optional(),
        }),
        z.object({
          afterDefinitionId: z.never().optional(),
          beforeDefinitionId: z
            .string()
            .describe(
              'Move the given `definitionId` before the referenced ticket definition.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          beforeDefinitionId: z.never().optional(),
          afterDefinitionId: z
            .string()
            .describe(
              'Move the given `definitionId` after the referenced ticket definition.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe('Optional fields.'),
});
export const ReorderTicketDefinitionsResponse = z.object({});
export const QueryTicketDefinitionsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          eventId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          revision: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          initialLimit: z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
            })
            .partial()
            .strict()
            .optional(),
          'pricingMethod.fixedPrice.value': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
            })
            .partial()
            .strict()
            .optional(),
          'pricingMethod.fixedPrice.currency': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          hidden: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $in: z.array(z.boolean()),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
            })
            .partial()
            .strict()
            .optional(),
          'pricingMethod.free': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $in: z.array(z.boolean()),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
            })
            .partial()
            .strict()
            .optional(),
          'pricingMethod.pricingType': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          feeType: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          saleStatus: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'eventId',
                'revision',
                'name',
                'description',
                'initialLimit',
                'pricingMethod.fixedPrice.value',
                'pricingMethod.fixedPrice.currency',
                '_createdDate',
                '_updatedDate',
                'hidden',
                'pricingMethod.free',
                'pricingMethod.pricingType',
                'feeType',
                'saleStatus',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe(
      'Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['SALES_DETAILS', 'EVENT_DETAILS', 'SEATING_DETAILS']))
        .max(5)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const QueryTicketDefinitionsResponse = z.object({
  ticketDefinitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Ticket definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        eventId: z
          .string()
          .describe('Event ID to which the ticket definition belongs.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the ticket definition is updated. To prevent conflicting changes, the existing `revision` must be used when updating a ticket definition.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the ticket definition was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the ticket definition was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Ticket definition name.')
          .min(1)
          .max(30)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Ticket definition description.')
          .max(500)
          .optional()
          .nullable(),
        policyText: z
          .string()
          .describe('Ticket definition policy.')
          .max(1000)
          .optional()
          .nullable(),
        hidden: z
          .boolean()
          .describe(
            "Whether this ticket definition is hidden from site visitors and can't be purchased."
          )
          .optional(),
        limited: z
          .boolean()
          .describe('Whether the ticket has a limited maximum quantity.')
          .optional(),
        initialLimit: z
          .number()
          .int()
          .describe(
            'The maximum number of tickets that can be sold for the event when first defining the event. If a seating map is defined after you created a ticket definition, this property is ignored and `actualLimit` is used instead. To create unlimited tickets, skip this field in the request.'
          )
          .optional()
          .nullable(),
        actualLimit: z
          .number()
          .int()
          .describe(
            'The maximum number of tickets that can be sold for the event after adding a seating map to the event. If no seating map is defined, this property is the same as `initialLimit`.'
          )
          .optional()
          .nullable(),
        pricingMethod: z
          .intersection(
            z.object({
              pricingType: z
                .enum(['STANDARD', 'DONATION'])
                .describe('Ticket price type.')
                .optional(),
              free: z
                .boolean()
                .describe(
                  'Whether the ticket is free. To create a free ticket, enter `"0"` in `pricingMethod.fixedPrice.value`.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                fixedPrice: z.never().optional(),
                guestPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
              }),
              z.object({
                guestPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                fixedPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                      )
                      .optional(),
                  })
                  .describe('Same ticket price for everyone.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                guestPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                      )
                      .optional(),
                  })
                  .describe(
                    'Guests choose how much they\'d like to pay for the ticket. You can set the minimum price, or specify `"0"` in the request to make the ticket free. The price can be updated to a higher amount by a guest during the checkout.'
                  ),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                guestPrice: z.never().optional(),
                pricingOptions: z
                  .object({
                    optionDetails: z
                      .array(
                        z.object({
                          optionId: z
                            .string()
                            .describe('Ticket price option ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe(
                              'Ticket price option name, such as "Child Ticket".'
                            )
                            .min(1)
                            .max(200)
                            .optional()
                            .nullable(),
                          price: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                                )
                                .optional(),
                            })
                            .describe('Ticket price.')
                            .optional(),
                        })
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe(
                    'Sets of various ticket prices. For example, you can charge different prices for children and adults.'
                  ),
              }),
            ])
          )
          .describe('Ticket pricing method.')
          .optional(),
        feeType: z
          .enum(['FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT', 'NO_FEE'])
          .describe('Type of ticket service fee to collect.')
          .optional(),
        salePeriod: z
          .object({
            startDate: z
              .date()
              .describe(
                'Date and time the ticket sale starts. Rounded down to the nearest minute.'
              )
              .optional()
              .nullable(),
            endDate: z
              .date()
              .describe(
                'Date and time the ticket sale ends. Rounded down to the nearest minute.'
              )
              .optional()
              .nullable(),
            displayNotOnSale: z
              .boolean()
              .describe(
                "Whether to display the ticket if it's not available to buy."
              )
              .optional(),
          })
          .describe('Ticket sale period.')
          .optional(),
        saleStatus: z
          .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
          .describe('Ticket sale status.')
          .optional(),
        salesDetails: z
          .object({
            unsoldCount: z
              .number()
              .int()
              .describe(
                "Number of tickets that haven't been purchased yet. The field is `null` if the ticket quantity is unlimited."
              )
              .optional()
              .nullable(),
            soldCount: z
              .number()
              .int()
              .describe('Number of purchased tickets.')
              .optional()
              .nullable(),
            reservedCount: z
              .number()
              .int()
              .describe('Number of reserved tickets.')
              .optional()
              .nullable(),
            soldOut: z
              .boolean()
              .describe('Whether the tickets are sold out.')
              .optional()
              .nullable(),
          })
          .describe(
            'Ticket sales information. <br> <br>\n**Note:** This field is only returned when `SALES_DETAILS` is specified in `field` in the request.'
          )
          .optional(),
        limitPerCheckout: z
          .number()
          .int()
          .describe(
            'Number of tickets that can be purchased per checkout. <br> <br>\n**Note:** If the `actualLimit` or `salesDetails.unsoldCount` field value is smaller than `limitPerCheckout`, then it overrides this field.'
          )
          .min(0)
          .max(50)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data extensions.')
          .optional(),
        eventDetails: z
          .object({
            title: z
              .string()
              .describe('Event title.')
              .min(1)
              .max(120)
              .optional()
              .nullable(),
            shortDescription: z
              .string()
              .describe('Short description of the event.')
              .max(500)
              .optional()
              .nullable(),
            location: z
              .object({
                name: z
                  .string()
                  .describe(
                    'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
                  )
                  .max(50)
                  .optional()
                  .nullable(),
                type: z
                  .enum(['VENUE', 'ONLINE'])
                  .describe('Location type.')
                  .optional(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Exact location address.')
                  .optional(),
                locationTbd: z
                  .boolean()
                  .describe('Whether the event location is TBD.')
                  .optional()
                  .nullable(),
              })
              .describe('Event location.')
              .optional(),
            dateAndTimeSettings: z
              .object({
                dateAndTimeTbd: z
                  .boolean()
                  .describe('Whether the event date and time are TBD.')
                  .optional()
                  .nullable(),
                dateAndTimeTbdMessage: z
                  .string()
                  .describe(
                    'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                startDate: z
                  .date()
                  .describe(
                    'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                  )
                  .optional()
                  .nullable(),
                endDate: z
                  .date()
                  .describe(
                    'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                  )
                  .optional()
                  .nullable(),
                timeZoneId: z
                  .string()
                  .describe(
                    'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                hideEndDate: z
                  .boolean()
                  .describe(
                    'Whether the end date is hidden in the formatted date and time.'
                  )
                  .optional()
                  .nullable(),
                showTimeZone: z
                  .boolean()
                  .describe(
                    'Whether the time zone is displayed in the formatted schedule.'
                  )
                  .optional()
                  .nullable(),
                recurrenceStatus: z
                  .enum([
                    'ONE_TIME',
                    'RECURRING',
                    'RECURRING_UPCOMING',
                    'RECURRING_RECENTLY_ENDED',
                    'RECURRING_RECENTLY_CANCELED',
                  ])
                  .describe('Repeating event status.')
                  .optional(),
                recurringEvents: z
                  .object({
                    individualEventDates: z
                      .array(
                        z.object({
                          startDate: z
                            .date()
                            .describe('Event start date.')
                            .optional()
                            .nullable(),
                          endDate: z
                            .date()
                            .describe('Event end date.')
                            .optional()
                            .nullable(),
                          timeZoneId: z
                            .string()
                            .describe(
                              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                            )
                            .max(100)
                            .optional()
                            .nullable(),
                          showTimeZone: z
                            .boolean()
                            .describe(
                              'Whether the time zone is displayed in a formatted schedule.'
                            )
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                    categoryId: z
                      .string()
                      .describe(
                        'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Event repetitions.')
                  .optional(),
                formatted: z
                  .object({
                    dateAndTime: z
                      .string()
                      .describe(
                        'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'Formatted start date of the event. Empty for TBD schedules.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                    startTime: z
                      .string()
                      .describe(
                        'Formatted start time of the event. Empty for TBD schedules.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                    endTime: z
                      .string()
                      .describe(
                        'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                  })
                  .describe('Formatted date and time settings.')
                  .optional(),
              })
              .describe('Event date and time settings.')
              .optional(),
            eventPageUrl: z
              .string()
              .describe('Event page URL components.')
              .optional(),
            status: z
              .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
              .describe('Event status.')
              .optional(),
          })
          .describe(
            'Information about the even the ticket is for. <br>\n**Note:** This field is only returned when `EVENT_DETAILS` is specified in `field` in the request.'
          )
          .optional(),
        seatingDetails: z
          .object({
            places: z
              .array(
                z.object({
                  placeId: z
                    .string()
                    .describe(
                      'Place ID in the format `{sectionId}-{elementId}-{label}`. For example, `0-1-A5`.'
                    )
                    .min(1)
                    .max(20)
                    .optional(),
                  label: z
                    .string()
                    .describe(
                      'Human-readable label for this place, such as `A1`, `12`, or `VIP1`.'
                    )
                    .min(1)
                    .max(4)
                    .optional(),
                  sectionLabel: z
                    .string()
                    .describe(
                      'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
                    )
                    .min(1)
                    .max(20)
                    .optional()
                    .nullable(),
                  elementLabel: z
                    .string()
                    .describe(
                      'Seating element within the section, such as `Row`, `Table`, or `General Admission`.'
                    )
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  availableCapacity: z
                    .number()
                    .int()
                    .describe(
                      'Available capacity at this place.\n\nFor general seating type returns the number of remaining spots.\n\nFor other types returns `1`. For example, if a table has 5 chairs, each chair is treated as a separate place, which has a capacity of 1.'
                    )
                    .min(0)
                    .max(50000)
                    .optional(),
                })
              )
              .max(50000)
              .optional(),
          })
          .describe(
            'Seating information including available seats and areas for this ticket definition. <br>\n**Note:** This field is only returned when `SEATING_DETAILS` is specified in `field` in the request, and when the event has a [seating plan](https://support.wix.com/en/article/wix-events-creating-a-seating-map).'
          )
          .optional(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(36)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(36)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const QueryAvailableTicketDefinitionsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1", "fieldName2":{"$operator":"value2"} }`. <br/> <br/> **Example:** <br/> `"filter" : { "id": "2224a9d1-79e6-4549-a5c5-bf7ce5aac1a5", "revision": {"$ne":"1"} }`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(100)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(10)
          .optional(),
      }),
      z.xor([
        z.object({
          paging: z.never().optional(),
          cursorPaging: z.never().optional(),
        }),
        z.object({
          cursorPaging: z.never().optional(),
          paging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load per page.')
                .min(0)
                .optional()
                .nullable(),
              offset: z
                .number()
                .int()
                .describe('Number of items to skip in the current sort order.')
                .min(0)
                .optional()
                .nullable(),
            })
            .describe('Paging options to limit and skip the number of items.'),
        }),
        z.object({
          paging: z.never().optional(),
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load per page.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort` parameters.'
            ),
        }),
      ])
    )
    .describe(
      'Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['SALES_DETAILS', 'EVENT_DETAILS', 'SEATING_DETAILS']))
        .max(5)
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const QueryAvailableTicketDefinitionsResponse = z.object({
  ticketDefinitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Ticket definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        eventId: z
          .string()
          .describe('Event ID to which the ticket definition belongs.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the ticket definition is updated. To prevent conflicting changes, the existing `revision` must be used when updating a ticket definition.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the ticket definition was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the ticket definition was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Ticket definition name.')
          .min(1)
          .max(30)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Ticket definition description.')
          .max(500)
          .optional()
          .nullable(),
        policyText: z
          .string()
          .describe('Ticket definition policy.')
          .max(1000)
          .optional()
          .nullable(),
        hidden: z
          .boolean()
          .describe(
            "Whether this ticket definition is hidden from site visitors and can't be purchased."
          )
          .optional(),
        limited: z
          .boolean()
          .describe('Whether the ticket has a limited maximum quantity.')
          .optional(),
        initialLimit: z
          .number()
          .int()
          .describe(
            'The maximum number of tickets that can be sold for the event when first defining the event. If a seating map is defined after you created a ticket definition, this property is ignored and `actualLimit` is used instead. To create unlimited tickets, skip this field in the request.'
          )
          .optional()
          .nullable(),
        actualLimit: z
          .number()
          .int()
          .describe(
            'The maximum number of tickets that can be sold for the event after adding a seating map to the event. If no seating map is defined, this property is the same as `initialLimit`.'
          )
          .optional()
          .nullable(),
        pricingMethod: z
          .intersection(
            z.object({
              pricingType: z
                .enum(['STANDARD', 'DONATION'])
                .describe('Ticket price type.')
                .optional(),
              free: z
                .boolean()
                .describe(
                  'Whether the ticket is free. To create a free ticket, enter `"0"` in `pricingMethod.fixedPrice.value`.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                fixedPrice: z.never().optional(),
                guestPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
              }),
              z.object({
                guestPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                fixedPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                      )
                      .optional(),
                  })
                  .describe('Same ticket price for everyone.'),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                pricingOptions: z.never().optional(),
                guestPrice: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                      )
                      .optional(),
                  })
                  .describe(
                    'Guests choose how much they\'d like to pay for the ticket. You can set the minimum price, or specify `"0"` in the request to make the ticket free. The price can be updated to a higher amount by a guest during the checkout.'
                  ),
              }),
              z.object({
                fixedPrice: z.never().optional(),
                guestPrice: z.never().optional(),
                pricingOptions: z
                  .object({
                    optionDetails: z
                      .array(
                        z.object({
                          optionId: z
                            .string()
                            .describe('Ticket price option ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe(
                              'Ticket price option name, such as "Child Ticket".'
                            )
                            .min(1)
                            .max(200)
                            .optional()
                            .nullable(),
                          price: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                                )
                                .optional(),
                              currency: z
                                .string()
                                .describe(
                                  'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (e.g., USD).'
                                )
                                .optional(),
                            })
                            .describe('Ticket price.')
                            .optional(),
                        })
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe(
                    'Sets of various ticket prices. For example, you can charge different prices for children and adults.'
                  ),
              }),
            ])
          )
          .describe('Ticket pricing method.')
          .optional(),
        feeType: z
          .enum(['FEE_INCLUDED', 'FEE_ADDED_AT_CHECKOUT', 'NO_FEE'])
          .describe('Type of ticket service fee to collect.')
          .optional(),
        salePeriod: z
          .object({
            startDate: z
              .date()
              .describe(
                'Date and time the ticket sale starts. Rounded down to the nearest minute.'
              )
              .optional()
              .nullable(),
            endDate: z
              .date()
              .describe(
                'Date and time the ticket sale ends. Rounded down to the nearest minute.'
              )
              .optional()
              .nullable(),
            displayNotOnSale: z
              .boolean()
              .describe(
                "Whether to display the ticket if it's not available to buy."
              )
              .optional(),
          })
          .describe('Ticket sale period.')
          .optional(),
        saleStatus: z
          .enum(['SALE_SCHEDULED', 'SALE_STARTED', 'SALE_ENDED'])
          .describe('Ticket sale status.')
          .optional(),
        salesDetails: z
          .object({
            unsoldCount: z
              .number()
              .int()
              .describe(
                "Number of tickets that haven't been purchased yet. The field is `null` if the ticket quantity is unlimited."
              )
              .optional()
              .nullable(),
            soldCount: z
              .number()
              .int()
              .describe('Number of purchased tickets.')
              .optional()
              .nullable(),
            reservedCount: z
              .number()
              .int()
              .describe('Number of reserved tickets.')
              .optional()
              .nullable(),
            soldOut: z
              .boolean()
              .describe('Whether the tickets are sold out.')
              .optional()
              .nullable(),
          })
          .describe(
            'Ticket sales information. <br> <br>\n**Note:** This field is only returned when `SALES_DETAILS` is specified in `field` in the request.'
          )
          .optional(),
        limitPerCheckout: z
          .number()
          .int()
          .describe(
            'Number of tickets that can be purchased per checkout. <br> <br>\n**Note:** If the `actualLimit` or `salesDetails.unsoldCount` field value is smaller than `limitPerCheckout`, then it overrides this field.'
          )
          .min(0)
          .max(50)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data extensions.')
          .optional(),
        eventDetails: z
          .object({
            title: z
              .string()
              .describe('Event title.')
              .min(1)
              .max(120)
              .optional()
              .nullable(),
            shortDescription: z
              .string()
              .describe('Short description of the event.')
              .max(500)
              .optional()
              .nullable(),
            location: z
              .object({
                name: z
                  .string()
                  .describe(
                    'Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.'
                  )
                  .max(50)
                  .optional()
                  .nullable(),
                type: z
                  .enum(['VENUE', 'ONLINE'])
                  .describe('Location type.')
                  .optional(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Exact location address.')
                  .optional(),
                locationTbd: z
                  .boolean()
                  .describe('Whether the event location is TBD.')
                  .optional()
                  .nullable(),
              })
              .describe('Event location.')
              .optional(),
            dateAndTimeSettings: z
              .object({
                dateAndTimeTbd: z
                  .boolean()
                  .describe('Whether the event date and time are TBD.')
                  .optional()
                  .nullable(),
                dateAndTimeTbdMessage: z
                  .string()
                  .describe(
                    'Message that is displayed when time and date is TBD.\n\n**Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                startDate: z
                  .date()
                  .describe(
                    'Event start date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                  )
                  .optional()
                  .nullable(),
                endDate: z
                  .date()
                  .describe(
                    'Event end date.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                  )
                  .optional()
                  .nullable(),
                timeZoneId: z
                  .string()
                  .describe(
                    'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.\n\n**Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                hideEndDate: z
                  .boolean()
                  .describe(
                    'Whether the end date is hidden in the formatted date and time.'
                  )
                  .optional()
                  .nullable(),
                showTimeZone: z
                  .boolean()
                  .describe(
                    'Whether the time zone is displayed in the formatted schedule.'
                  )
                  .optional()
                  .nullable(),
                recurrenceStatus: z
                  .enum([
                    'ONE_TIME',
                    'RECURRING',
                    'RECURRING_UPCOMING',
                    'RECURRING_RECENTLY_ENDED',
                    'RECURRING_RECENTLY_CANCELED',
                  ])
                  .describe('Repeating event status.')
                  .optional(),
                recurringEvents: z
                  .object({
                    individualEventDates: z
                      .array(
                        z.object({
                          startDate: z
                            .date()
                            .describe('Event start date.')
                            .optional()
                            .nullable(),
                          endDate: z
                            .date()
                            .describe('Event end date.')
                            .optional()
                            .nullable(),
                          timeZoneId: z
                            .string()
                            .describe(
                              'Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.'
                            )
                            .max(100)
                            .optional()
                            .nullable(),
                          showTimeZone: z
                            .boolean()
                            .describe(
                              'Whether the time zone is displayed in a formatted schedule.'
                            )
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                    categoryId: z
                      .string()
                      .describe(
                        'Recurring event category ID.\n\nThis read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe('Event repetitions.')
                  .optional(),
                formatted: z
                  .object({
                    dateAndTime: z
                      .string()
                      .describe(
                        'Formatted date and time representation. <br>\nExample of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>\nExample of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                    startDate: z
                      .string()
                      .describe(
                        'Formatted start date of the event. Empty for TBD schedules.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                    startTime: z
                      .string()
                      .describe(
                        'Formatted start time of the event. Empty for TBD schedules.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                    endDate: z
                      .string()
                      .describe(
                        'Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                    endTime: z
                      .string()
                      .describe(
                        'Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.'
                      )
                      .max(500)
                      .optional()
                      .nullable(),
                  })
                  .describe('Formatted date and time settings.')
                  .optional(),
              })
              .describe('Event date and time settings.')
              .optional(),
            eventPageUrl: z
              .string()
              .describe('Event page URL components.')
              .optional(),
            status: z
              .enum(['UPCOMING', 'STARTED', 'ENDED', 'CANCELED', 'DRAFT'])
              .describe('Event status.')
              .optional(),
          })
          .describe(
            'Information about the even the ticket is for. <br>\n**Note:** This field is only returned when `EVENT_DETAILS` is specified in `field` in the request.'
          )
          .optional(),
        seatingDetails: z
          .object({
            places: z
              .array(
                z.object({
                  placeId: z
                    .string()
                    .describe(
                      'Place ID in the format `{sectionId}-{elementId}-{label}`. For example, `0-1-A5`.'
                    )
                    .min(1)
                    .max(20)
                    .optional(),
                  label: z
                    .string()
                    .describe(
                      'Human-readable label for this place, such as `A1`, `12`, or `VIP1`.'
                    )
                    .min(1)
                    .max(4)
                    .optional(),
                  sectionLabel: z
                    .string()
                    .describe(
                      'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
                    )
                    .min(1)
                    .max(20)
                    .optional()
                    .nullable(),
                  elementLabel: z
                    .string()
                    .describe(
                      'Seating element within the section, such as `Row`, `Table`, or `General Admission`.'
                    )
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  availableCapacity: z
                    .number()
                    .int()
                    .describe(
                      'Available capacity at this place.\n\nFor general seating type returns the number of remaining spots.\n\nFor other types returns `1`. For example, if a table has 5 chairs, each chair is treated as a separate place, which has a capacity of 1.'
                    )
                    .min(0)
                    .max(50000)
                    .optional(),
                })
              )
              .max(50000)
              .optional(),
          })
          .describe(
            'Seating information including available seats and areas for this ticket definition. <br>\n**Note:** This field is only returned when `SEATING_DETAILS` is specified in `field` in the request, and when the event has a [seating plan](https://support.wix.com/en/article/wix-events-creating-a-seating-map).'
          )
          .optional(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(36)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(36)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const CountTicketDefinitionsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1" }`.'
        )
        .optional()
        .nullable(),
      facet: z.array(z.string()).max(20).optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const CountTicketDefinitionsResponse = z.object({
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(36)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(36)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
  facets: z
    .record(
      z.string(),
      z.object({
        counts: z
          .record(z.string(), z.number().int())
          .describe('Facet counts aggregated per value')
          .optional(),
      })
    )
    .describe('Filter facets.')
    .optional(),
});
export const CountAvailableTicketDefinitionsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1" }`.'
        )
        .optional()
        .nullable(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const CountAvailableTicketDefinitionsResponse = z.object({
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(36)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(36)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const BulkDeleteTicketDefinitionsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter object. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.'
    ),
});
export const BulkDeleteTicketDefinitionsByFilterResponse = z.object({});
export const ChangeCurrencyRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      currency: z
        .string()
        .describe(
          'Ticket price currency in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
        )
        .min(3)
        .max(3),
    })
    .describe('Optional fields.'),
});
export const ChangeCurrencyResponse = z.object({});
